import type { GetViewpointResult } from "features/viewpoints/types";

import { procoreClient } from "./client";

type Uuid = string;

export type BimModelHomeView = {
    id: Uuid;
    bim_viewpoint_id: Uuid;
};

type BimModelSummary = {
    id: Uuid;
    home_view: BimModelHomeView | null;
};

/**
 * Fetches all models for a project (with home-view summary) and returns the
 * matching model entry for the given modelId.
 */
export async function getModelHomeView(params: {
    companyId: number;
    projectId: number;
    modelId: Uuid;
}): Promise<BimModelHomeView | null> {
    const { companyId, projectId, modelId } = params;

    const response = await procoreClient<{ data: BimModelSummary[] }>(
        `/rest/v2.0/companies/${companyId}/projects/${projectId}/bim/models?lifecycle_state=PUBLISHED&include_viewpoint_summary=true`,
        { method: "GET" },
    );

    const model = response.data.find((m) => m.id === modelId);
    return model?.home_view ?? null;
}

/**
 * Saves the current viewpoint as the model home view.
 *
 * - If the model has no home view yet, creates a new model viewpoint and
 *   assigns it as the home via `set_as_model_home: true`.
 * - If a home view already exists, patches the existing model viewpoint's
 *   underlying viewpoint payload in place.
 */
export async function saveModelHomeViewpoint(params: {
    companyId: number;
    projectId: number;
    modelId: Uuid;
    sceneId: Uuid;
    viewpoint: GetViewpointResult;
    existingHomeView: BimModelHomeView | null;
}): Promise<void> {
    const { companyId, projectId, modelId, sceneId, viewpoint, existingHomeView } = params;

    if (existingHomeView) {
        await procoreClient<unknown>(
            `/rest/v2.0/companies/${companyId}/projects/${projectId}/bim/models/${modelId}/model_viewpoints/${existingHomeView.id}`,
            {
                method: "PATCH",
                body: JSON.stringify({
                    viewpoint: {
                        name: "Home View",
                        payload: {
                            ...viewpoint,
                            name: "Home View",
                        },
                    },
                }),
            },
        );
    } else {
        await procoreClient<unknown>(
            `/rest/v2.0/companies/${companyId}/projects/${projectId}/bim/models/${modelId}/model_viewpoints`,
            {
                method: "POST",
                body: JSON.stringify({
                    viewpoint: {
                        name: "Home View",
                        scene_id: sceneId,
                        payload: {
                            ...viewpoint,
                            name: "Home View",
                        },
                    },
                    set_as_model_home: true,
                }),
            },
        );
    }
}
